import styled, { css } from "styled-components";
import {
  Calendar as CalendarPrimitive,
  DateInput as DateInputPrimitive,
  DateSegment as DateSegmentPrimitive,
  Dialog as DialogPrimitive,
} from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";

export const Container = styled.div`
  ${() => {
    const { tokens } = useDesignTokens();
    const { datePicker } = tokens;

    return css`
      width: 100%;

      .react-aria-Group {
        display: flex;
        align-items: center;
        gap: ${datePicker.gap};
        min-height: ${datePicker.input.minHeight};
        background: ${datePicker.input.backgroundColor};
        padding-left: ${datePicker.input.padding.left};
        padding-right: ${datePicker.input.padding.right};
        border-radius: ${datePicker.input.radius};
        outline-width: ${datePicker.input.stroke.width.base};
        outline-style: ${datePicker.input.stroke.style};
        outline-color: ${datePicker.input.stroke.color.base};
        outline-offset: ${datePicker.input.stroke.offset.base};
      }

      .react-aria-DatePicker {
        &[data-invalid] .react-aria-Group {
          outline-width: ${datePicker.input.stroke.width.error};
          outline-color: ${datePicker.input.stroke.color.error};
          outline-offset: ${datePicker.input.stroke.offset.error};
        }

        &[data-focus-within] .react-aria-Group {
          outline-width: ${datePicker.input.stroke.width.focus};
          outline-color: ${datePicker.input.stroke.color.focus};
          outline-offset: ${datePicker.input.stroke.offset.focus};
        }
      }

      .react-aria-Button {
        all: unset;
        width: max-content;
        height: max-content;
        border-radius: ${datePicker.input.button.radius};
        padding: ${datePicker.input.button.padding};
        background: ${datePicker.input.button.backgroundColor.base};
        cursor: pointer;

        svg {
          display: block;
          width: ${datePicker.input.button.icon.width};
          height: ${datePicker.input.button.icon.height};
          color: ${datePicker.input.button.icon.color.base};
        }

        &[data-pressed],
        &[data-focused] {
          background: ${datePicker.input.button.backgroundColor.focus};

          svg {
            color: ${datePicker.input.button.icon.color.focus};
          }
        }

        &[data-hovered] {
          background: ${datePicker.input.button.backgroundColor.hover};

          svg {
            color: ${datePicker.input.button.icon.color.hover};
          }
        }
      }
    `;
  }}
`;

export const DateInput = styled(DateInputPrimitive)`
  display: flex;
  flex: 1;
`;

export const DateSegment = styled(DateSegmentPrimitive)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { datePicker } = tokens;

    return css`
      padding-top: ${datePicker.input.segment.padding.top};
      padding-left: ${datePicker.input.segment.padding.left};
      padding-right: ${datePicker.input.segment.padding.right};
      padding-bottom: ${datePicker.input.segment.padding.bottom};

      &[inputmode="numeric"] {
        border-radius: ${datePicker.input.segment.radius};
        color: ${datePicker.input.segment.color.base};
        caret-color: transparent;
        white-space: nowrap;
        outline: none;

        &[data-placeholder] {
          text-transform: uppercase;
          color: ${datePicker.input.segment.placeholderColor};
        }

        &[data-focused] {
          color: ${datePicker.input.segment.color.focus};
          background: ${datePicker.input.segment.backgroundColor.focus};
        }

        &[data-hovered] {
          color: ${datePicker.input.segment.color.hover};
          background: ${datePicker.input.segment.backgroundColor.hover};
        }
      }

      &[data-type="literal"] {
        color: ${datePicker.input.segment.separatorColor};
      }
    `;
  }}
`;

export const Dialog = styled(DialogPrimitive)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { datePicker } = tokens;

    return css`
      padding: ${datePicker.calendar.padding};
      background: ${datePicker.calendar.backgroundColor};
      border-radius: ${datePicker.calendar.radius};
      border-width: ${datePicker.calendar.stroke.width};
      border-style: ${datePicker.calendar.stroke.style};
      border-color: ${datePicker.calendar.stroke.color};
      max-height: inherit;
      overflow-y: auto;

      z-index: ${datePicker.calendar.zIndex};
    `;
  }}
`;

export const Calendar = styled(CalendarPrimitive)`
  ${() => {
    const { tokens } = useDesignTokens();
    const { datePicker } = tokens;

    return css`
      display: grid;
      gap: ${datePicker.calendar.gap};
      cursor: default;

      header {
        display: grid;
        place-items: center;
        grid-template-columns: auto 1fr auto;
        margin-left: ${datePicker.calendar.header.margin.left};
        margin-right: ${datePicker.calendar.header.margin.right};
      }

      .react-aria-Button {
        all: unset;
        width: max-content;
        height: max-content;
        border-radius: ${datePicker.calendar.button.radius};
        padding-top: ${datePicker.calendar.button.padding.top};
        padding-left: ${datePicker.calendar.button.padding.left};
        padding-right: ${datePicker.calendar.button.padding.right};
        padding-bottom: ${datePicker.calendar.button.padding.bottom};
        background: ${datePicker.calendar.button.backgroundColor.base};
        cursor: pointer;

        svg {
          display: block;
          width: ${datePicker.calendar.button.icon.width};
          height: ${datePicker.calendar.button.icon.height};
          color: ${datePicker.calendar.button.icon.color.base};
        }

        &[data-hovered] {
          background: ${datePicker.calendar.button.backgroundColor.hover};

          svg {
            color: ${datePicker.calendar.button.icon.color.hover};
          }
        }

        &[data-pressed] {
          background: ${datePicker.calendar.button.backgroundColor.focus};

          svg {
            color: ${datePicker.calendar.button.icon.color.focus};
          }
        }

        &[data-focus-visible] {
          outline-width: ${datePicker.calendar.button.focusRing.width};
          outline-style: ${datePicker.calendar.button.focusRing.style};
          outline-color: ${datePicker.calendar.button.focusRing.color};
          outline-offset: ${datePicker.calendar.button.focusRing.offset};
        }
      }

      .react-aria-CalendarCell {
        display: grid;
        place-items: center;
        min-width: ${datePicker.calendar.cell.minWidth};
        margin: ${datePicker.calendar.cell.margin};
        color: ${datePicker.calendar.cell.color.base};
        background: ${datePicker.calendar.cell.backgroundColor.base};
        padding: ${datePicker.calendar.cell.padding};
        border-radius: ${datePicker.calendar.cell.radius};
        cursor: pointer;
        outline: none;

        &[data-unavailable] {
          color: ${datePicker.calendar.cell.color.disabled};
          cursor: not-allowed;
        }

        &[data-outside-month] {
          display: none;
        }

        &[data-selected]:not([data-unavailable]) {
          color: ${datePicker.calendar.cell.color.active};
          background: ${datePicker.calendar.cell.backgroundColor.active};
        }

        &[data-hovered]:not([data-unavailable]) {
          color: ${datePicker.calendar.cell.color.hover};
          background: ${datePicker.calendar.cell.backgroundColor.hover};
        }

        &[data-pressed]:not([data-unavailable]) {
          color: ${datePicker.calendar.cell.color.active};
          background: ${datePicker.calendar.cell.backgroundColor.active};
        }

        &[data-focus-visible] {
          outline-width: ${datePicker.calendar.cell.focusRing.width};
          outline-style: ${datePicker.calendar.cell.focusRing.style};
          outline-color: ${datePicker.calendar.cell.focusRing.color};
          outline-offset: ${datePicker.calendar.cell.focusRing.offset};
        }
      }
    `;
  }}
`;
